import { MSCartIcon } from '@wolfejs/icons/IconMap';

import { Badge } from '@wolfejs/ui/Badge';
import { CopyToClipboard } from '@wolfejs/ui/CopyToClipboard/CopyToClipboard';
import { FormatCurrency } from '@wolfejs/ui/Format/FormatCurrency';
import { Image } from '@wolfejs/ui/Image';
import type { CellContext } from '@wolfejs/ui/Table';
import type { TableSchemaEntry } from '@wolfejs/ui/Table/types';
import { UI } from '@wolfejs/ui/UI';
import { UIVariants } from '@wolfejs/ui/types/UI';
import { cn } from '@wolfejs/ui/utils/classnames';
import React from 'react';
import { dateTimeDisplayFormat, formatDate } from './date';
import { getBadgeColorByStatus } from './getBadgeColorByStatus';
import { toFloat, toInteger } from './number';
import { toggleIsApprovedForBuying } from './toggleIsApprovedForBuying';

export type RoleEntity = {
  id?: string;
  name: string;
};

interface ObjectWithId extends Object {
  id: string;
}

export const getFormattedValue = <DataType extends Object | undefined = {}>(
  key: string,
  value: unknown,
  schema?: Record<string, string | TableSchemaEntry>,
  info?: CellContext<DataType, unknown>
): React.ReactNode => {
  let schemaValueStr: string | undefined = undefined;
  let schemaValueDef: TableSchemaEntry | undefined = undefined;
  if (typeof schema?.[key] === 'string') {
    schemaValueStr = schema?.[key] as string;
  } else {
    schemaValueDef = schema?.[key] as TableSchemaEntry;
  }

  switch (schemaValueDef?.id || schema?.[key]) {
    case 'action':
      return <div className={`mr-4 ${schemaValueDef?.className} ${schemaValueStr}`}>{value as string}</div>;
    case 'thumbnail':
      return <Image src={value as string} className="h-6" alt={key} />;
    case 'badge':
      return (
        <Badge variant={UIVariants.solid} color={getBadgeColorByStatus(value as string)}>
          {value as string}
        </Badge>
      );
    case 'gift-card-display':
      return (
        <div className="flex gap-2">
          <img src={info?.row.getValue('image')} alt={key} className="mr-2 h-6 w-10 rounded-sm object-cover" />
          <span>{value as string}</span>
        </div>
      );
    case 'stock':
      return (
        <div className="flex content-center items-center gap-2">
          <div className={cn(' h-3 w-3 rounded-full', getBgColor(value as number))} />
          <span>{value as string}</span>
        </div>
      );
    case 'sales':
      return (
        <div className="flex content-center items-center gap-1">
          <MSCartIcon className={cn('text-default')} />
          <FormatCurrency
            useHumanReadable
            decimalScale={2}
            numberFormatBaseProps={{ value: (value as number) / 100, displayType: 'text' }}
          />
        </div>
      );
    case 'float':
      return toFloat({ value: value as number });
    case 'currency':
      return (
        <FormatCurrency
          prefix="$"
          decimalScale={2}
          numberFormatBaseProps={{ value: (value as number) / 100, displayType: 'text' }}
        />
      );
    case 'human-currency':
      return (
        <FormatCurrency
          useHumanReadable
          decimalScale={2}
          prefix="$"
          numberFormatBaseProps={{ value: value as number, displayType: 'text' }}
        />
      );
    case 'human-currency2':
      return (
        <FormatCurrency
          useHumanReadable
          prefix="$"
          decimalScale={2}
          numberFormatBaseProps={{ value: (value as number) / 100, displayType: 'text' }}
        />
      );
    case 'discount':
      return toFloat({ value: (value as number) / 100, prefix: '(', suffix: ')' });
    case 'human-discount':
      return toFloat({ value: (value as number), prefix: '(', suffix: ')' });
    case 'integer':
      return toInteger({ value: value as number });
    case 'email':
      return (
        <a className="text-default-500" href={`mailto:${value}`}>
          {value as string}
        </a>
      );
    case 'role':
      return (
        <div className=" flex flex-col">
          <div>{`${(value as RoleEntity)?.name || ''}`}</div>
          {/* <div
            className="cursor-pointer"
            onClick={() => copy(value as string, { onCopy: () => UI.notifications.info('copied') })}
          >{`${value?.id.length > 10 ? value?.id.slice(0, 10) : value?.id || ''}`}</div> */}
        </div>
      );

    case 'date':
      return <span>{formatDate(new Date(value as string))}</span>;
    case 'datetime':
      return <span>{formatDate(new Date(value as string), dateTimeDisplayFormat)}</span>;
    case 'id':
      return <span>{`#${value}`}</span>;
    case 'valueIndicator':
      return <span>{`${value && value != 'false' ? '🟢' : '🔴'}`}</span>;
    case 'showless':
      return (
        <CopyToClipboard
          text={(value as string) || ''}
          onCopy={() => UI.notifications.info('Copied to clipboard')}
          className="cursor-pointer"
        >
          <span>{`${(value as string)?.length > 10 ? `${(value as string)?.slice(0, 10)}...` : value}`}</span>
        </CopyToClipboard>
      );
    case 'uuid':
      return (
        <CopyToClipboard
          text={(value as string) || ''}
          onCopy={() => UI.notifications.info('Copied to clipboard')}
          className="cursor-pointer"
        >
          <span>{`${(value as string)?.slice(0, 6)}...${(value as string)?.slice(
            (value as string).length - 7,
            (value as string).length - 1
          )}`}</span>
        </CopyToClipboard>
      );
    case 'copy-to-clipboard':
      return (
        <CopyToClipboard
          text={(value as string) || ''}
          onCopy={() => UI.notifications.info('Copied to clipboard')}
          className="cursor-pointer"
        >
          <span>{value as string}</span>
        </CopyToClipboard>
      );
    default:
      if (typeof value === 'object') return <span>*OBJ*</span>;
      if (typeof value === 'boolean') {
        const theOriginal = info?.row?.original as ObjectWithId;
        if (theOriginal.id) {
          return toggleIsApprovedForBuying(value, theOriginal.id);
        }
      }
      return <span>{value as string}</span>;
  }
};

const DangerThrashHold = 50;
const CriticalThrashHold = 100;
const WarningThrashHold = 200;
export const getBgColor = (value: number) => {
  if (value < DangerThrashHold) {
    return 'bg-error-700';
  }
  if (value < CriticalThrashHold) {
    return 'bg-error-500';
  }
  if (value < WarningThrashHold) {
    return 'bg-warning-300';
  }
  return 'bg-success';
};
